import { Component, OnDestroy, OnInit } from '@angular/core'
import moment from 'moment'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { KeySystemNavConfig } from '../../../configuration/key.system.nav.config'
import { KeySystem, Structure } from '../../../models/dev.test.models'
import { ErrorService } from '../../../services/error.service'
import { KeyingAuthService } from '../../../services/keying.auth.service'
import { KeyingDataSets, KeyingDataSetsService, KeySystemDataSets } from '../../../services/keying.data.sets.service'
import { KeyingStructureService } from '../../../services/keying.structure.service'
import { KeyingSystemService } from '../../../services/keying.system.service'
import { LoadingService } from '../../../services/loading.service'
import { User } from '@overtur-monorepo/keying-user'
import { DialogService } from '../../../services/dialog.service'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { EditNoteComponent } from '../../../dialogs/edit-note-dialog/edit-note-dialog.component'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MatTableDataSource } from '@angular/material/table'
import { NoteType } from '../../../constants/noteType.enum'
import { LoggingService } from '../../../services/logging.service'
import { Router } from '@angular/router'
import { AppRoutingConfig } from '../../../configuration/app.routing.config'
import { ChangeLogConfirmationDialogComponent } from '../../../components/matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'
import { NotesService } from '../../../services/notes/notes.service'
import { FeatureFlagService, ToastService } from '@overtur-monorepo/keying-shared'

/**
 * Dashboard component for key system management
 * Handles structure/key system data display, notes management, and structure operations
 * Uses dedicated services for data management and follows token-based loading pattern
 */
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<boolean>()

  structure: Structure
  keySystem: KeySystem
  keyingDataSets: KeyingDataSets
  keySystemDataSets: KeySystemDataSets
  keywayFamily
  combinatingRule
  user: User
  newNotecontent
  dateSent: Date
  email: string
  orderNumber: string

  notes = []
  dataSource: MatTableDataSource<any> = new MatTableDataSource([])
  expanded = true
  noteType = 1
  activeFilterButton = ''
  basicDisplayedColumns: string[] = [
    'modifiedOn',
    'createdBy',
    'noteType',
    'message',
    'email',
    'orderNumber',
    'dateSent',
    'actions',
  ]
  basicDisplayedColumnFilters: string[] = [
    'modifiedOn-filter',
    'createdBy-filter',
    'noteType-filter',
    'message-filter',
    'email-filter',
    'orderNumber-filter',
    'dateSent-filter',
    'actions-filter',
  ]
  displayedColumns: string[] = [
    'modifiedOn',
    'createdBy',
    'noteType',
    'description',
    'email',
    'orderNumber',
    'dateSent',
    'delete',
  ]
  displayedColumnFilters: string[] = [
    'modifiedOn-filter',
    'createdBy-filter',
    'noteType-filter',
    'description-filter',
    'email-filter',
    'orderNumber-filter',
    'dateSent-filter',
    'delete-filter',
  ]

  private hasStructure = false
  private hasKeySystem = false

  noteForm = new FormGroup({
    dateSent: new FormControl(),
    orderNumber: new FormControl(),
    email: new FormControl(),
    newNote: new FormControl(),
  })

  constructor(
    private router: Router,
    private keyingAuthService: KeyingAuthService,
    private keyingStructureService: KeyingStructureService,
    private keyingSystemService: KeyingSystemService,
    private errorService: ErrorService,
    private loadingService: LoadingService,
    private keyingDataSetsService: KeyingDataSetsService,
    private keySystemNavConfig: KeySystemNavConfig,
    private dialogService: DialogService,
    private loggingService: LoggingService,
    private notesService: NotesService,
    private featureFlagService: FeatureFlagService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.setupStructureSubscription()
    this.setupKeySystemSubscription()
    this.setupUserSubscription()
    this.setupKeySystemDataSetsSubscription()
    this.setupKeyingDataSetsSubscription()
    this.setupNotesDataSourceSubscription()
    this.activeFilterButton = 'Structure'
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  private setupStructureSubscription(): void {
    this.keyingStructureService.structure$.pipe(takeUntil(this.destroy$)).subscribe(
      (structure: Structure) => {
        this.structure = structure
        this.hasStructure = !!structure
        if (this.structure) {
          this.getNotes(false)
        }
        this.checkIfReadyForNavigation()
      },
      (error) => {
        this.errorService.throw(error)
      }
    )
  }

  private setupKeySystemSubscription(): void {
    this.keyingSystemService.keySystem$.pipe(takeUntil(this.destroy$)).subscribe(
      (keySystem: KeySystem) => {
        this.keySystem = keySystem
        this.hasKeySystem = !!keySystem
        this.checkIfReadyForNavigation()
      },
      (error) => {
        this.errorService.throw(error)
      }
    )
  }

  private checkIfReadyForNavigation(): void {
    if (this.hasStructure && this.hasKeySystem && this.structure && this.keySystem) {
      this.keySystemNavConfig.setNav('Dashboard', this.structure, this.keySystem)
    }
  }

  private setupUserSubscription(): void {
    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (!user) {
        return
      }
      this.user = user
    })
  }

  private setupKeySystemDataSetsSubscription(): void {
    this.keyingDataSetsService.keySystemDataSets$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      if (!res) {
        return
      }
      this.keySystemDataSets = res
    })
  }

  private setupKeyingDataSetsSubscription(): void {
    this.keyingDataSetsService.keyingDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((keyingDataSets: KeyingDataSets) => {
        if (!keyingDataSets || !this.structure) {
          return
        }
        this.keyingDataSets = keyingDataSets
        this.keywayFamily = this.keyingDataSets.keywayFamilies.find((kwf) => kwf.id === this.structure?.keywayFamilyId)
        this.combinatingRule = this.keyingDataSets.combinatingRuleSets.find(
          (kwf) => kwf.id === this.structure?.combinatingRuleSetId
        )
      })
  }

  private getNotes(forceReload: boolean = false): void {
    if (!this.structure?.id) {
      return
    }
    this.notesService.loadNotesForStructure(this.structure.id, forceReload)
  }

  private setupNotesDataSourceSubscription(): void {
    this.notesService.dataSource$.pipe(takeUntil(this.destroy$)).subscribe((dataSource) => {
      this.dataSource = dataSource

      // Set up custom filter predicate for column-specific filtering
      if (this.dataSource) {
        this.dataSource.filterPredicate = (data: any, filter: string) => {
          try {
            const filterObj = JSON.parse(filter)
            const columnValue = this.getColumnValue(data, filterObj.column)
            return columnValue.toLowerCase().includes(filterObj.value)
          } catch {
            // Fallback to global filter if not JSON
            const dataStr = Object.keys(data)
              .reduce((currentTerm: string, key: string) => {
                return currentTerm + (data as { [key: string]: any })[key] + '◬'
              }, '')
              .toLowerCase()
            const transformedFilter = filter.trim().toLowerCase()
            return dataStr.indexOf(transformedFilter) !== -1
          }
        }
      }

      if (this.activeFilterButton && dataSource && dataSource.data && dataSource.data.length > 0) {
        this.notesService.filterTableByType(this.activeFilterButton)
      }
    })

    this.notesService.notes$.pipe(takeUntil(this.destroy$)).subscribe((notes) => {
      this.notes = notes
    })
  }

  applyFilter(event?: Event) {
    const filterValue = (event.target as HTMLInputElement).value
    this.notesService.applyFilter(filterValue)
  }

  applyColumnFilter(event: Event, column: string) {
    const filterValue = (event.target as HTMLInputElement).value
    if (this.dataSource) {
      this.dataSource.filter = JSON.stringify({
        column: column,
        value: filterValue.trim().toLowerCase(),
      })
    }
  }

  filterTable(type) {
    this.activeFilterButton = type
    this.notesService.filterTableByType(type)
  }

  /* istanbul ignore next */
  checkValidators(type: NoteType) {
    if (type === NoteType.BittingList) {
      console.log('BittingList selected')

      this.noteForm.get('dateSent').setValidators(Validators.required)
      this.noteForm.get('orderNumber').setValidators(Validators.required)
      this.noteForm.get('email').setValidators(Validators.required)
    } else {
      console.log('Not BittingList selected')

      this.noteForm.get('dateSent').clearValidators()
      this.noteForm.get('orderNumber').clearValidators()
      this.noteForm.get('email').clearValidators()
    }

    // Update validity after changing validators
    this.noteForm.get('dateSent').updateValueAndValidity()
    this.noteForm.get('orderNumber').updateValueAndValidity()
    this.noteForm.get('email').updateValueAndValidity()
  }

  updateNoteType(type: number | null) {
    console.log('Updating note type to:', type)
    this.noteType = type
    this.dateSent = null
    this.email = null
    this.orderNumber = null
    this.checkValidators(type)
  }

  public getDisplayDate(givenDate) {
    const ts = moment.utc(givenDate)

    givenDate = moment(ts).format('MMMM D YYYY')
    const today = moment().format('MMMM D YYYY')
    const yesterday = moment().subtract(1, 'days').format('MMMM D YYYY')

    if (givenDate === today) {
      return 'Today'
    }

    if (givenDate === yesterday) {
      return 'Yesterday'
    }

    return givenDate
  }

  postNote() {
    if (this.noteForm.invalid) {
      return
    }

    const noteRequest = this.buildNoteRequest()
    this.performNoteCreate(noteRequest)
  }

  private buildNoteRequest() {
    return {
      message: this.noteForm.get('newNote').value,
      noteType: this.noteType,
      dateSent: this.noteForm.get('dateSent').value,
      email: this.noteForm.get('email').value,
      orderNumber: this.noteForm.get('orderNumber').value,
    }
  }

  private performNoteCreate(noteRequest) {
    this.notesService
      .createNote(noteRequest, this.structure.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.errorService.confirm('Note created successfully')
          this.clearNoteForm()
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  private clearNoteForm(): void {
    this.noteForm.reset()
    // Clear component properties for backward compatibility
    this.newNotecontent = null
    this.dateSent = null
    this.email = null
    this.orderNumber = null
    //update Note type so Bitting List note form resets
    this.updateNoteType(NoteType.Structure)
  }

  editNote(note) {
    const dialogRef = this.dialogService.open(EditNoteComponent, note, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.notesService
          .updateNote(note.id, result.note.message, this.structure.id)
          .pipe(take(1))
          .subscribe({
            next: () => {
              // Note updated successfully - NotesService handles refresh automatically
              this.errorService.confirm('Note updated successfully')
              this.getNotes(true)
            },
            error: (error) => {
              this.errorService.throw(error)
            },
          })
      }
    })
  }

  deleteNote(note) {
    const confirmData = this.createDeleteNoteConfirmData(note)
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, confirmData, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        this.performNoteDelete(note)
      }
    })
  }

  private createDeleteNoteConfirmData(note) {
    return {
      title: 'Delete Note',
      message: `Are you sure you want to delete note created by ${note.createdBy}?`,
      cancel: 'Cancel',
      confirm: 'Delete',
      showCancel: true,
      reasonRequired: false,
    }
  }

  private performNoteDelete(note) {
    this.notesService
      .deleteNote(note.id, this.structure.id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          // Note deleted successfully - NotesService handles refresh automatically
        },
        error: (error) => {
          this.errorService.throw(error)
        },
      })
  }

  /**
   * Business rule check - determines if current user can delete the structure
   * Feature flag (DELETE_STRUCTURE) controls WHO can access this feature (role-based)
   * This method controls WHEN they can delete (business rules: ownership)
   */
  canDeleteStructure(): boolean {
    // Only check business logic - NO role checks here
    // Role-based access control is handled by the DELETE_STRUCTURE feature flag
    return this.user?.id === this.keySystem?.lockedById
  }

  deleteStructure(structure: Structure) {
    // Defense in depth: Double-check business rules before proceeding
    if (!this.canDeleteStructure()) {
      this.toastService.showToast({ title: 'Error', subTitle: 'Cannot delete structure: User does not own the lock', type: 'error' })
      return
    }

    const confirmData = {
      title: 'Delete Structure',
      message: `Are you sure you want to delete this structure and all associated key systems?`,
      cancel: 'Cancel',
      confirm: 'Delete',
      actionType: 'Delete Keysystem',
      showCancel: true,
      reasonRequired: true,
    }
    const dialogRef = this.dialogService.open(ChangeLogConfirmationDialogComponent, confirmData, false)

    dialogRef.afterClosed().subscribe((result) => {
      if (result.confirmed) {
        const auditRequestFormData = {
          auditTrailRequestDto: {
            reasonCode: result.auditTrailRequest.reasonCode,
            description: result.auditTrailRequest.description,
          },
        }
        this.loadingService.set({ isLoading: true, message: 'Deleting Structure' })
        this.keyingStructureService
          .deleteStructure(structure.id, auditRequestFormData)
          .pipe(take(1))
          .subscribe(
            () => {
              this.router.navigate([AppRoutingConfig.structuresUrl])
              this.loadingService.set({ isLoading: false, message: '' })
            },
            (error) => {
              this.errorService.throw(error)
              this.loadingService.set({ isLoading: false, message: '' })
            }
          )
      }
    })
  }

  checkDisable(note) {
    if (this.user.isSuperUser) {
      return false
    } else if (this.user.id === note.createdById) {
      return false
    } else {
      return true
    }
  }

  showHideUpgradeStructureButton(structure: Structure) {
    return (
      this.user.id === this.keySystem.lockedById &&
      this.keySystem.isPreviouslyPublished &&
      (structure.keywayFamilyId === 5 || structure.keywayFamilyId === 6 || structure.keywayFamilyId === 7)
    )
  }

  upgradeStructure(structure: Structure) {
    this.loadingService.set({ isLoading: true, message: 'Upgrading Structure' })
    this.keyingStructureService
      .upgradeStructure(structure.id)
      .pipe(take(1))
      .subscribe(
        () => {
          this.refreshPage()
          this.loadingService.set({ isLoading: false, message: '' })
        },
        (error) => {
          this.errorService.throw(error)
          this.loadingService.set({ isLoading: false, message: '' })
        }
      )
  }

  refreshPage() {
    window.location.reload()
  }

  trackByFn(index: number, item: any) {
    return item.id || index
  }

  private getColumnValue(data: any, column: string): string {
    switch (column) {
      case 'modifiedOn':
        return data.modifiedOn || data.createdOn || ''
      case 'createdBy':
        return data.createdBy || ''
      case 'noteType':
        return data.noteTypeName || ''
      case 'message':
        return data.message || ''
      case 'email':
        return data.email || ''
      case 'orderNumber':
        return data.orderNumber || ''
      case 'dateSent':
        return data.dateSent || ''
      default:
        return ''
    }
  }
}
